import { defineFeature, defineModule, definePermission } from '@shared/contract'

// Verkauf an Geschäftskunden und Lieferung: Angebot → Auftrag → Lieferschein → Rücknahme; Kommission für Feste,
// Verleih von Geräten, Auftragseingang der Bestell-App. Die Kasse und die Rechnung gehören anderen Modulen – der
// Verkauf endet beim zugestellten, abrechenbaren Lieferschein.
//
// Rechte – alle je Standort vergebbar (db/seeds/demo/roles.ts, Abschnitt „Verkauf“):
//   * Auftrag bestätigen heißt reservieren. Ein Rabatt über `maxDiscountBp` der Person wartet auf die Freigabe durch
//     jemanden, dessen Grenze ihn deckt (`discount.grant`); über dem Kreditlimit bestätigt nur, wer
//     `creditLimit.override` hält.
//   * `delivery.write` kommissioniert und bucht den Lieferschein am abgebenden Standort; `delivery.confirm` bestätigt
//     die Zustellung – der Fahrer hält es an seinem Fahrzeug und sieht nur dessen Lieferungen.
//   * Rücknahmen (`return.create`) liest, wer Lieferscheine liest.
//   * Per E-Mail versenden (`document.send`, docs/anmeldung/SPEZIFIKATION.md §5.2): Angebot, Auftragsbestätigung,
//     Lieferschein – ein Recht für alle drei, je Standort.
//   * Lieferregeln und Gutscheine (§19): `checkout.read` sieht Zeitfenster, Liefergebiete, Gutscheine und die Belegung (auch der Zugang
//     der Bestell-App, der die Regeln abruft), `checkout.write` pflegt sie. Die Einlösung eines Gutscheins läuft innerhalb der
//     Bestellung (`order.write`) und braucht kein eigenes Recht.
//   * Markt-Dashboard (§20, Abschnitt 3a): `pickup.handover` prüft Abholcodes und gibt die Ware aus (die Liste der Abholungen liest, wer
//     Aufträge lesen darf); Marktdaten (Öffnungszeiten, Hinweis) pflegt, wer Lieferregeln pflegt (`checkout.write`).
//   * Markt-Dashboard (§20, Abschnitt 3b): `case.read` sieht die Anliegen der Bestell-App, die Abos und den Kasten „Bestell-App“ am Kunden (auch der
//     Zugang der Bestell-App, der die Anliegen abholt), `case.write` entscheidet Anträge und erteilt der App Aufträge – beides gilt für den ganzen Betrieb.
//   * Disposition (docs/integration/bestell-app.md §18): `tour.read` sieht die Touren (der Fahrer nur die seines Fahrzeugs),
//     `tour.write` plant sie, `tour.report` hält nur der technische Zugang der Bestell-App.
//   * Telefonassistent (docs/integration/telefonassistent.md §4): `phone.read`, `phone.lead` und `phone.orderLookup` hält nur der
//     technische Zugang mit der Rolle „Telefonassistent“ (`api_phone`, angelegt von scripts/channel-setup.ts --channel vocaris). Die
//     Verträge `sales.phone.*` prüfen zusätzlich, dass der Aufrufer ein solcher Zugang ist (die Geschäftsführung hält über '*' alle Rechte).
//     `phone.order` (Telefon-Bestellungen, unbestätigt) vergibt das Einrichtungsskript nur mit `--allow-orders` (telefonassistent.md §16).
const GROUP = 'Verkauf'

export default defineModule({
  id: 'sales',
  title: 'Verkauf',
  description: 'Angebote, Aufträge mit Reservierung und Kreditlimit, Kommissionierung, Lieferscheine, Zustellung, Rücknahmen, Auftragseingang angebundener Systeme – je nach Funktionen auch Kommissionsware und Verleih.',
  dependsOn: ['platform', 'masterdata'],
  // Pfand & Leergut ist ein Branchenmodul: Der Verkauf läuft auch ohne (Leergut an der Tür nur mit ctx.feature('deposits'));
  // Preise: das Ereignis pricing.price.changed führt zu sales.channel.catalogChanged (Bestell-App)
  // Lager: Leihbestand festlegen (Veranstaltungen) und Umlagerung an eine Filiale aus einer E-Mail anfordern (Anfragen-Zentrale,
  // docs/anfragen-zentrale/SPEZIFIKATION.md §7.3)
  optionalDependsOn: ['deposits', 'pricing', 'inventory'],
  features: [
    defineFeature({ key: 'rental', label: 'Leihartikel', description: 'Verleih mit Kalender, Kaution und Rückgabe.', defaultEnabled: false }),
    defineFeature({ key: 'consignment', label: 'Kommission', description: 'Ware auf Kommission liefern und nach Rückgabe abrechnen (Festservice).', defaultEnabled: false }),
    // Veranstaltungen (docs/veranstaltungen/SPEZIFIKATION.md): braucht „Kommission“ und „Leihartikel“ (server/eventFeatures.ts)
    defineFeature({
      key: 'events',
      label: 'Veranstaltungen',
      description: 'Veranstaltungen als eigener Vorgang: Anfrage, Angebot, Zusage, Lieferung auf Kommission mit Leihartikeln, Abholung und Abrechnung mit Gebühren.',
      defaultEnabled: false,
    }),
    // Anfragen-Zentrale (docs/anfragen-zentrale/SPEZIFIKATION.md): eingehende E-Mails (Postfach: Einstellungen › E-Mail-Eingang)
    defineFeature({
      key: 'mailrequests',
      label: 'Anfragen per E-Mail',
      description: 'Eingehende E-Mails als Anliegen im Verkauf: Bestellung, Veranstaltung oder Filiale erkennen und weiterverarbeiten.',
      defaultEnabled: false,
      effect: 'Eingehende E-Mails werden zu Anliegen „E-Mail“ unter Verkauf › Anfragen per E-Mail; ohne den Schalter bleiben sie nur im E-Mail-Eingang.',
    }),
  ],
  permissions: [
    definePermission({ key: 'sales.quote.read', label: 'Angebote lesen', group: GROUP }),
    definePermission({ key: 'sales.quote.write', label: 'Angebote anlegen, ändern und abgeben', group: GROUP }),
    definePermission({
      key: 'sales.order.read',
      label: 'Aufträge lesen',
      group: GROUP,
      description: 'Aufträge des Standorts mit bestellten, reservierten, gelieferten und rückständigen Mengen.',
    }),
    definePermission({
      key: 'sales.order.write',
      label: 'Aufträge anlegen, ändern und bestätigen',
      group: GROUP,
      description: 'Bestätigen reserviert den Bestand. Rabatte über der eigenen Grenze und Aufträge über dem Kreditlimit warten auf Freigabe.',
    }),
    definePermission({
      key: 'sales.delivery.read',
      label: 'Lieferscheine und Rücknahmen lesen',
      group: GROUP,
      description: 'Am abgebenden Standort oder am Fahrzeug der Tour.',
    }),
    definePermission({
      key: 'sales.delivery.write',
      label: 'Kommissionieren und Lieferscheine buchen',
      group: GROUP,
      description: 'Bucht den Warenausgang; ordnet Lieferungen einem Fahrzeug zu.',
    }),
    definePermission({
      key: 'sales.delivery.confirm',
      label: 'Zustellung bestätigen',
      group: GROUP,
      description: 'Übergabe beim Kunden: geliefert wie notiert oder mit Abweichungen, Empfänger, Kassieren an der Tür.',
    }),
    definePermission({
      key: 'sales.return.create',
      label: 'Rücknahmen vom Kunden buchen',
      group: GROUP,
      description: 'Ware zurück in den Bestand oder in den Sperrbestand.',
    }),
    definePermission({
      key: 'sales.discount.grant',
      label: 'Rabatte gewähren und freigeben',
      group: GROUP,
      description: 'Bis zur Grenze ohne Freigabe; Aufträge anderer mit höherem Rabatt bis zu dieser Grenze freigeben.',
      limits: [{ key: 'maxDiscountBp', label: 'Höchster Rabatt je Position', unit: 'bp' }],
    }),
    definePermission({
      key: 'sales.document.send',
      label: 'Angebote, Auftragsbestätigungen und Lieferscheine per E-Mail versenden',
      group: GROUP,
      description: 'Der Beleg steht im Mailtext. Aufträge, die über die App des Betriebs eingehen, bestätigt die App selbst.',
    }),
    definePermission({ key: 'sales.consignment.read', label: 'Kommissionen lesen', group: GROUP }),
    definePermission({
      key: 'sales.consignment.write',
      label: 'Kommissionsware liefern und abrechnen',
      group: GROUP,
      description: 'Ware zum Kunden auf Kommission, Rückzählung nach dem Fest; berechnet wird nur der Verbrauch.',
    }),
    definePermission({ key: 'sales.rental.read', label: 'Verleih und Verfügbarkeit lesen', group: GROUP }),
    definePermission({
      key: 'sales.rental.write',
      label: 'Leihartikel buchen, ausgeben und zurücknehmen',
      group: GROUP,
    }),
    definePermission({
      key: 'sales.tour.read',
      label: 'Touren lesen',
      group: GROUP,
      description: 'Disposition: Touren mit Stopps, Route und Live-Stand. Der Fahrer sieht nur die Touren seines Fahrzeugs; der Vertriebskanal ruft sie über die Kanal-Verträge ab.',
    }),
    definePermission({
      key: 'sales.tour.write',
      label: 'Touren planen und freigeben',
      group: GROUP,
      description: 'Disposition: Touren anlegen, automatisch planen, Stopps festlegen, Fahrer zuweisen, freigeben, abbrechen und abschließen.',
    }),
    definePermission({
      key: 'sales.tour.report',
      label: 'Tourmeldungen des Vertriebskanals entgegennehmen',
      group: GROUP,
      description: 'Nur für den technischen Zugang des Vertriebskanals: Start, Ankunft, Ende und Position der Fahrzeuge melden.',
    }),
    definePermission({
      key: 'sales.checkout.read',
      label: 'Lieferregeln und Gutscheine lesen',
      group: GROUP,
      description: 'Zeitfenster mit Belegung, Ausnahmetage, Liefergebiete, Tragepauschale, „Lieferung frei“ und Gutscheine; der Vertriebskanal ruft sie für seinen Spiegel ab.',
    }),
    definePermission({
      key: 'sales.checkout.write',
      label: 'Lieferregeln und Gutscheine pflegen',
      group: GROUP,
      description: 'Zeitfenster und Kapazität, Ausnahmetage, Bestellschluss, Liefergebiete mit Gebühren, Tragepauschale, „Lieferung frei“ und Gutscheincodes ändern.',
    }),
    definePermission({
      key: 'sales.pickup.handover',
      label: 'Abholungen prüfen und ausgeben',
      group: GROUP,
      description: 'Abholcode prüfen und die Ware an der Theke übergeben, Alterskontrolle und Zahlung an der Theke. Schließt das Buchen von Lieferschein und Zustellung für diese Abholung ein.',
    }),
    definePermission({
      key: 'sales.case.read',
      label: 'Anliegen des Vertriebskanals lesen',
      group: GROUP,
      locationScoped: false,
      description: 'Geschäftskunden-Anträge, Löschanträge und Aufträge an den Vertriebskanal, dessen Abos, Einwilligungen und Konten am Kunden. Der Vertriebskanal ruft die Anliegen über die Kanal-Verträge ab.',
    }),
    definePermission({
      key: 'sales.case.write',
      label: 'Anträge des Vertriebskanals entscheiden',
      group: GROUP,
      locationScoped: false,
      description: 'Geschäftskunden-Antrag annehmen (Kunde anlegen oder zuordnen) oder ablehnen, Löschantrag ausführen oder ablehnen, dem Vertriebskanal Aufträge erteilen (Konto zuordnen, Widerruf eintragen, Abo pausieren oder fortsetzen).',
    }),
    definePermission({
      key: 'sales.mailRequest.manage',
      label: 'Anfragen per E-Mail einrichten',
      group: GROUP,
      locationScoped: false,
      description: 'KI-Einordnung und automatische Übernahme von Bestellungen ein- und ausschalten, Absenderprüfung, Standorte und die E-Mail-Adressen der Filialen festlegen.',
    }),
    definePermission({
      key: 'sales.phone.read',
      label: 'Telefonassistent: Sortiment, Preise und Lieferregeln abfragen',
      group: GROUP,
      description:
        'Nur für den technischen Zugang des Telefonassistenten: Sortiment, Preise und Bestand zum gebundenen Sammelkunden, Lieferregeln ohne Gutscheincodes und „Lieferung frei“, Belegung der Zeitfenster, Gutschein prüfen.',
    }),
    definePermission({
      key: 'sales.phone.lead',
      label: 'Telefonassistent: Interessenten melden',
      group: GROUP,
      description: 'Nur für den technischen Zugang des Telefonassistenten: Interessenten als Anliegen melden und den Stand der eigenen Meldungen abrufen.',
    }),
    definePermission({
      key: 'sales.phone.orderLookup',
      label: 'Telefonassistent: Auftragsstand mit Auftragsnummer und Postleitzahl abfragen',
      group: GROUP,
      description: 'Nur für den technischen Zugang des Telefonassistenten: Stand und Zeitfenster eines Auftrags – nur mit Nummer und passender Postleitzahl, mit Sperre nach Fehlversuchen.',
    }),
    definePermission({
      key: 'sales.phone.order',
      label: 'Telefonassistent: Telefon-Bestellungen anlegen (unbestätigt)',
      group: GROUP,
      description:
        'Nur für den technischen Zugang des Telefonassistenten und nur mit ausdrücklicher Freigabe (--allow-orders): Geschäftskunden an Kundennummer und Postleitzahl erkennen (nur ja oder nein), einen Warenkorb prüfen und rechnen, eine Bestellung im Zustand „Eingegangen“ anlegen. Nichts wird reserviert; der Vertrieb bestätigt. Keine Kundenlisten, keine Rechnungen, kein Storno.',
    }),
    definePermission({
      key: 'sales.creditLimit.override',
      label: 'Aufträge über dem Kreditlimit freigeben',
      group: GROUP,
      description: 'Bestätigt einen Auftrag trotz überschrittenem Kreditlimit – mit den Zahlen im Protokoll.',
    }),
    // Belege nach der Zustellung (docs/belege-nach-zustellung/SPEZIFIKATION.md): Schalter des Betriebs, alle standardmäßig aus
    definePermission({
      key: 'sales.automation.manage',
      label: 'Automatiken des Verkaufs einstellen',
      group: GROUP,
      locationScoped: false,
      description: 'Eingehende Bestellungen des Vertriebskanals (App) bzw. des Telefonassistenten automatisch bestätigen und den Lieferschein nach der Zustellung automatisch per E-Mail senden.',
    }),
    // Veranstaltungen (docs/veranstaltungen/SPEZIFIKATION.md §4): lesen, Anfragen anlegen (auch der Zugang der Bestell-App und der
    // Anfragen-Zentrale), bearbeiten (Angebot, Zusage, Lieferung, Abrechnung), Übergabe und Abholung melden (Fahrer, Bestell-App),
    // Gebührenregeln pflegen (für den ganzen Betrieb). Die Schritte rufen die vorhandenen Vorgänge auf – wer sie auslöst, braucht
    // deren Rechte am Standort (Angebot, Auftrag, Kommission, Verleih, Leergut).
    definePermission({ key: 'sales.event.read', label: 'Veranstaltungen lesen', group: GROUP, description: 'Anfragen, Angebote, Zusagen, Lieferung, Abholung und Abrechnung von Veranstaltungen am liefernden Standort.' }),
    definePermission({ key: 'sales.event.create', label: 'Veranstaltungsanfragen anlegen', group: GROUP, description: 'Eine Anfrage als Veranstaltung erfassen – von Hand, aus einer E-Mail, einem Anruf oder der Bestell-App.' }),
    definePermission({
      key: 'sales.event.write',
      label: 'Veranstaltungen bearbeiten und abwickeln',
      group: GROUP,
      description: 'Anfragen ändern, Angebot erstellen, Zusage erfassen, Lieferung buchen, Abholung erfassen, abrechnen und absagen.',
    }),
    definePermission({
      key: 'sales.event.report',
      label: 'Übergabe und Abholung einer Veranstaltung melden',
      group: GROUP,
      description: 'Für Fahrer am Fahrzeug der Tour und den Zugang der Bestell-App: Übergabe bestätigen, Rückware, Leergut und Leihartikel bei der Abholung zählen.',
    }),
    definePermission({
      key: 'sales.event.settings',
      label: 'Gebührenregeln der Veranstaltungen pflegen',
      group: GROUP,
      locationScoped: false,
      description: 'Gebühren für Rückgaben und Anfahrt sowie die Anbindung der Bestell-App an Veranstaltungen festlegen.',
    }),
  ],
})
